import { ArrowBack, DarkMode, Hub, LightMode, Logout, Menu as MenuIcon, PersonAddAlt1 } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useAuth } from '../../contexts/AuthContext'
import { useThemeMode } from '../../contexts/ThemeContext'

export interface HeaderProps {
  /** Optional title to display (e.g., book name) */
  title?: string
  /** Optional font family for title text */
  titleFontFamily?: string
  /** Optional direction for title text rendering */
  titleDirection?: 'ltr' | 'rtl'
  /** Optional actions to render before the title */
  actionsBeforeTitle?: React.ReactNode
  /** Optional actions to render after the title */
  actionsAfterTitle?: React.ReactNode
  /** Show back button instead of logo */
  showBackButton?: boolean
  /** Back button click handler */
  onBackClick?: () => void
  /** Menu-style actions to render after the title (like File, Edit, View in Google Docs) */
  actions?: React.ReactNode
  /** Optional toolbar/content to render below the header bar */
  toolbarSlot?: React.ReactNode
  /** Hide default navigation links */
  hideNavigation?: boolean
  /** Hide the brand/logo block on mobile */
  hideBrandOnMobile?: boolean
  /** Hide the hidden-navigation hamburger menu on mobile */
  hideHiddenNavigationMenuOnMobile?: boolean
  /** Optional hierarchy for mobile title navigation (root to most nested) */
  mobileTitleHierarchy?: string[]
}

// Menu button style (consistent across all menu items)
const menuButtonSx = {
  textTransform: 'none',
  color: 'var(--text-primary)',
  fontWeight: 400,
  fontSize: '14px',
  px: 1.5,
  minWidth: 'auto',
  '&:hover': { bgcolor: 'var(--bg-hover)' },
}

const Header: React.FC<HeaderProps> = ({
  title,
  titleFontFamily,
  titleDirection,
  actionsBeforeTitle,
  actionsAfterTitle,
  showBackButton = false,
  onBackClick,
  actions,
  toolbarSlot,
  hideNavigation = false,
  hideBrandOnMobile = false,
  hideHiddenNavigationMenuOnMobile = false,
  mobileTitleHierarchy,
}) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user, isAuthenticated, logout } = useAuth()
  const { resolvedMode, setMode } = useThemeMode()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [navAnchorEl, setNavAnchorEl] = React.useState<null | HTMLElement>(null)
  const showBrand = !(hideBrandOnMobile && isMobile)
  const showHiddenNavigationMenu =
    hideNavigation &&
    !(hideHiddenNavigationMenuOnMobile && isMobile) &&
    (user?.role === 'author' || user?.role === 'admin')
  const effectiveTitleDirection = titleDirection || 'ltr'
  const mobileRtlTitleLayout = isMobile && effectiveTitleDirection === 'rtl'
  const hasSplitActions = actionsBeforeTitle !== undefined || actionsAfterTitle !== undefined
  const mobileTitleScrollRef = React.useRef<HTMLSpanElement | null>(null)
  const mobileTitleLastSegmentRef = React.useRef<HTMLSpanElement | null>(null)
  const normalizedMobileTitleHierarchy = React.useMemo(
    () => (mobileTitleHierarchy || []).map((item) => item.trim()).filter(Boolean),
    [mobileTitleHierarchy],
  )
  const canUseMobileHierarchy = isMobile && normalizedMobileTitleHierarchy.length > 0
  const mobileHierarchyKey = normalizedMobileTitleHierarchy.join('\u0001')

  React.useEffect(() => {
    if (!canUseMobileHierarchy) {
      return
    }

    const container = mobileTitleScrollRef.current
    const lastSegment = mobileTitleLastSegmentRef.current
    if (!container || !lastSegment) {
      return
    }

    const frame = window.requestAnimationFrame(() => {
      // Always reveal the most nested breadcrumb (last item) on first render/update.
      lastSegment.scrollIntoView({ inline: 'end', block: 'nearest', behavior: 'auto' })
    })

    return () => window.cancelAnimationFrame(frame)
  }, [canUseMobileHierarchy, mobileHierarchyKey])

  const displayedTitle = title || 'The Last Manifesto'
  const displayedTitleTooltip = canUseMobileHierarchy ? normalizedMobileTitleHierarchy.join(' / ') : displayedTitle

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setNavAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleNavClose = () => {
    setNavAnchorEl(null)
  }

  const handleLogout = () => {
    logout()
    handleClose()
    navigate('/')
  }

  const handleToggleTheme = () => {
    setMode(resolvedMode === 'dark' ? 'light' : 'dark')
    handleClose()
  }

  const handleBack = () => {
    if (onBackClick) {
      onBackClick()
    } else {
      navigate(-1)
    }
  }

  const renderActionSlot = (content: React.ReactNode, order?: { xs: number; md: number }) => {
    if (!content) {
      return null
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          flex: { xs: '0 0 auto', md: 1 },
          overflow: { xs: 'visible', md: 'hidden' },
          order,
        }}
      >
        {content}
      </Box>
    )
  }

  return (
    <Box sx={{ borderBottom: '1px solid var(--border-primary)' }} dir="ltr">
      {/* Main header bar - always LTR regardless of content direction */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: { xs: 52, md: 56 },
          px: { xs: 1, md: 2 },
          bgcolor: 'var(--bg-surface)',
          direction: 'ltr',
          gap: { xs: 0.5, md: 0 },
        }}
      >
        {/* Left section: Logo + Hamburger (when nav hidden) + Title + Menu Actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            order: mobileRtlTitleLayout ? { xs: 2, md: 0 } : undefined,
          }}
        >
          {showBackButton ? (
            <IconButton onClick={handleBack} sx={{ marginInlineEnd: 1 }}>
              <ArrowBack />
            </IconButton>
          ) : showBrand ? (
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  bgcolor: import.meta.env.DEV ? '#e65100' : '#1976d2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginInlineEnd: 1.5,
                }}
              >
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18, lineHeight: 1 }}>M</Typography>
              </Box>
            </Box>
          ) : null}

          {/* Hamburger menu for navigation when hideNavigation is true */}
          {showHiddenNavigationMenu && (
            <>
              <IconButton onClick={handleNavMenu} sx={{ color: 'var(--icon-default)', marginInlineEnd: 1 }}>
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={navAnchorEl}
                open={Boolean(navAnchorEl)}
                onClose={handleNavClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {(user?.role === 'author' || user?.role === 'admin') && (
                  <MenuItem component={Link} to="/people" onClick={handleNavClose}>
                    <PersonAddAlt1 sx={{ mr: 1, color: 'var(--icon-default)' }} /> People
                  </MenuItem>
                )}
                {(user?.role === 'author' || user?.role === 'admin') && (
                  <MenuItem component={Link} to="/graph" onClick={handleNavClose}>
                    <Hub sx={{ mr: 1, color: 'var(--icon-default)' }} /> Graph
                  </MenuItem>
                )}
              </Menu>
            </>
          )}

          {hasSplitActions && renderActionSlot(actionsBeforeTitle, mobileRtlTitleLayout ? { xs: 1, md: 0 } : undefined)}

          {/* Title or App name */}
          <Typography
            variant="h6"
            noWrap
            component={title ? 'span' : Link}
            to={title ? undefined : '/'}
            dir={title ? effectiveTitleDirection : undefined}
            title={displayedTitleTooltip}
            sx={{
              fontWeight: 500,
              fontFamily: titleFontFamily || 'inherit',
              color: 'var(--text-primary)',
              textDecoration: 'none',
              fontSize: title ? { xs: '16px', md: '18px' } : '16px',
              maxWidth: title ? { xs: 'none', md: 300 } : 'auto',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              flex: title ? { xs: '1 1 auto', md: '0 1 auto' } : '0 1 auto',
              order: mobileRtlTitleLayout ? { xs: 2, md: 0 } : undefined,
              textAlign: effectiveTitleDirection === 'rtl' ? 'right' : 'left',
              unicodeBidi: 'plaintext',
              marginInlineEnd: { xs: 0.5, md: 2 },
            }}
          >
            {canUseMobileHierarchy ? (
              <Box
                ref={mobileTitleScrollRef}
                component="span"
                dir={effectiveTitleDirection}
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    pr: 0.5,
                  }}
                >
                  {normalizedMobileTitleHierarchy.map((segment, index) => {
                    const isLast = index === normalizedMobileTitleHierarchy.length - 1
                    return (
                      <React.Fragment key={`${segment}-${index}`}>
                        {index > 0 && (
                          <Box
                            component="span"
                            aria-hidden="true"
                            sx={{
                              width: 4,
                              height: 4,
                              borderRadius: '50%',
                              bgcolor: 'var(--text-tertiary)',
                              opacity: 0.75,
                              flex: '0 0 auto',
                            }}
                          />
                        )}
                        <Box
                          component="span"
                          ref={isLast ? mobileTitleLastSegmentRef : undefined}
                          sx={{
                            color: isLast ? 'var(--text-primary)' : 'var(--text-secondary)',
                            fontWeight: 400,
                            letterSpacing: isLast ? 0 : '0.008em',
                            opacity: isLast ? 1 : 0.9,
                          }}
                        >
                          {segment}
                        </Box>
                      </React.Fragment>
                    )
                  })}
                </Box>
              </Box>
            ) : (
              displayedTitle
            )}
          </Typography>

          {!hideNavigation && (
            <Box
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                alignItems: 'center',
                gap: 0.5,
                flexShrink: 0,
              }}
            >
              {isAuthenticated && (user?.role === 'author' || user?.role === 'admin') && (
                <>
                  <Button component={Link} to="/people" size="small" sx={menuButtonSx}>
                    People
                  </Button>
                  <Button component={Link} to="/graph" size="small" sx={menuButtonSx}>
                    Graph
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Custom menu-style actions (like Publish, Book Settings in editor) */}
          {hasSplitActions
            ? renderActionSlot(actionsAfterTitle, mobileRtlTitleLayout ? { xs: 3, md: 0 } : undefined)
            : renderActionSlot(actions, mobileRtlTitleLayout ? { xs: 1, md: 0 } : undefined)}
        </Box>

        {/* Right section: Account / Auth */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.25, md: 1 },
            flexShrink: 0,
            order: mobileRtlTitleLayout ? { xs: 1, md: 0 } : undefined,
          }}
        >
          {isAuthenticated ? (
            <>
              <IconButton
                size="small"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                sx={{ p: { xs: 0.25, md: 0.5 } }}
              >
                <Avatar
                  sx={{
                    width: { xs: 24, md: 32 },
                    height: { xs: 24, md: 32 },
                    bgcolor: 'var(--bg-hover)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-subtle)',
                    fontSize: { xs: '0.9rem', md: '1rem' },
                  }}
                >
                  {user?.username?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: mobileRtlTitleLayout ? 'left' : 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: mobileRtlTitleLayout ? 'left' : 'right' }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                {isMobile && isAuthenticated && (user?.role === 'author' || user?.role === 'admin') && (
                  <>
                    <MenuItem component={Link} to="/people" onClick={handleClose}>
                      <PersonAddAlt1 sx={{ mr: 1, color: 'var(--icon-default)' }} /> People
                    </MenuItem>
                    <MenuItem component={Link} to="/graph" onClick={handleClose}>
                      <Hub sx={{ mr: 1, color: 'var(--icon-default)' }} /> Graph
                    </MenuItem>
                    <Divider />
                  </>
                )}
                <MenuItem onClick={handleToggleTheme}>
                  {resolvedMode === 'dark' ? <LightMode sx={{ mr: 1 }} /> : <DarkMode sx={{ mr: 1 }} />}
                  {resolvedMode === 'dark' ? 'Light mode' : 'Dark mode'}
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 1 }} /> Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Tooltip title={resolvedMode === 'dark' ? 'Light mode' : 'Dark mode'}>
                <IconButton
                  size="small"
                  onClick={() => setMode(resolvedMode === 'dark' ? 'light' : 'dark')}
                  sx={{ color: 'var(--icon-default)' }}
                >
                  {resolvedMode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Button component={Link} to="/login" sx={{ color: 'var(--text-secondary)', textTransform: 'none' }}>
                Login
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Toolbar slot (full-width, below header bar) */}
      {toolbarSlot}
    </Box>
  )
}

export default Header
